import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg, fmtPrice } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useStore } from "../context/StoreContext";
import { CourseCard } from "../components/CourseCard";
import { CourseThumb, Stars, fmtCount } from "../lib/art";
import { toast } from "sonner";

export default function CartPage({ wishlistMode = false }) {
  const { user } = useAuth();
  const { cart, bundleCart, wishlist, toggleCart, toggleBundle, toggleWishlist, clearCart, currency, sections } = useStore();
  const ids = wishlistMode ? wishlist : cart;
  const [courses, setCourses] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [provider, setProvider] = useState("stripe");
  const [paypalOn, setPaypalOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  const [coupon, setCoupon] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/courses").then((r) => setCourses(r.data)).catch(() => {});
    api.get("/bundles").then((r) => setBundles(r.data)).catch(() => {});
    api.get("/payments/config").then((r) => setPaypalOn(r.data.paypal)).catch(() => {});
  }, []);
  useEffect(() => { setCoupon(null); }, [cart, bundleCart, currency]);

  const secMap = Object.fromEntries(sections.map((s) => [s.id, s]));
  const items = ids.map((id) => courses.find((c) => c.id === id)).filter(Boolean);
  const bundleItems = wishlistMode ? [] : bundleCart.map((id) => bundles.find((b) => b.id === id)).filter(Boolean);
  const total = items.reduce((n, c) => n + c.effective_price, 0) + bundleItems.reduce((n, b) => n + b.price, 0);
  const old = items.reduce((n, c) => n + Math.max(c.price, c.effective_price), 0) + bundleItems.reduce((n, b) => n + b.total_value, 0);
  const count = items.length + bundleItems.length;
  const conv = (v) => fmtPrice(v, currency);

  const applyCoupon = async () => {
    if (!user) return nav("/auth?next=/cart");
    if (!couponInput.trim()) return;
    try {
      const { data } = await api.post("/coupons/validate", { code: couponInput.trim(), course_ids: cart, bundle_ids: bundleCart, currency });
      setCoupon(data);
      toast.success(`Coupon ${data.code} applied — ${data.percent_off}% off!`);
    } catch (e) { setCoupon(null); toast.error(errMsg(e)); }
  };

  const checkout = async () => {
    if (!user) return nav("/auth?next=/cart");
    setBusy(true);
    try {
      const ep = provider === "paypal" ? "/payments/paypal/create" : "/payments/checkout";
      const { data } = await api.post(ep, { course_ids: cart, bundle_ids: bundleCart, currency, origin_url: window.location.origin, coupon_code: coupon?.code || null });
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
      clearCart();
      toast.success("Enrolled — you're in!");
      nav("/my-learning");
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };

  if (wishlistMode) {
    return (
      <div className="wrap cart-page">
        <h1 data-testid="wishlist-title">MY LIST ♥</h1>
        <p className="sub">{items.length} saved course{items.length !== 1 ? "s" : ""}</p>
        {items.length === 0 ? (
          <div className="empty" data-testid="empty-state">Nothing saved yet — tap the ♡ on any course card.<br /><br />
            <button className="btn-primary" onClick={() => nav("/courses")}>Browse Courses</button></div>
        ) : (
          <div className="results-grid">{items.map((c) => <CourseCard key={c.id} c={c} secMap={secMap} />)}</div>
        )}
      </div>
    );
  }

  return (
    <div className="wrap cart-page">
      <h1 data-testid="cart-title">SHOPPING CART</h1>
      <p className="sub">{count} item{count !== 1 ? "s" : ""} in cart</p>
      {count === 0 ? (
        <div className="empty" data-testid="empty-state">Your cart is empty.<br /><br />
          <button className="btn-primary" onClick={() => nav("/courses")}>Browse Courses</button></div>
      ) : (
        <div className="cart-layout">
          <div>
            {bundleItems.map((b) => (
              <div className="cart-item" key={b.id} data-testid={`cart-bundle-${b.id}`}>
                <div className="ct bundle-thumb">🎁</div>
                <div className="ci-body">
                  <h3>{b.title} <span className="bundle-tag">BUNDLE · SAVE {b.savings_pct}%</span></h3>
                  <div className="instructor">{b.courses.map((c) => c.title).join(" + ")}</div>
                  <button className="rm" data-testid={`remove-bundle-${b.id}`} onClick={() => toggleBundle(b.id)}>Remove</button>
                </div>
                <div className="cp-price">{conv(b.price)} <span className="bundle-old-sm">{conv(b.total_value)}</span></div>
              </div>
            ))}
            {items.map((c) => (
            <div className="cart-item" key={c.id} data-testid={`cart-item-${c.id}`}>
              <div className="ct" onClick={() => nav(`/course/${c.id}`)}><CourseThumb course={c} color={secMap[c.section]?.color} /></div>
              <div className="ci-body">
                <h3 onClick={() => nav(`/course/${c.id}`)}>{c.title}</h3>
                <div className="instructor">By {c.instructor}</div>
                <div className="rating-row"><span className="rv">{(c.rating || 0).toFixed(1)}</span><Stars r={c.rating || 0} /><span className="rc">({fmtCount(c.rating_count)})</span></div>
                <button className="mv" onClick={() => { toggleCart(c.id); if (!wishlist.includes(c.id)) toggleWishlist(c.id); }}>Save for later</button>
                <button className="rm" data-testid={`remove-item-${c.id}`} onClick={() => toggleCart(c.id)}>Remove</button>
              </div>
              <div className="cp-price">{conv(c.effective_price)}</div>
            </div>
          ))}
          </div>
          <div className="cart-sum">
            <span className="lbl">TOTAL</span>
            <div className="total" data-testid="cart-total">{coupon ? conv(total * (1 - coupon.percent_off / 100)) : conv(total)}</div>
            {coupon && <div className="save" data-testid="coupon-applied-line">Coupon {coupon.code}: −{coupon.percent_off}% ({currency === "EUR" ? "€" : "$"}{coupon.discount.toFixed(2)} off)</div>}
            {old > total && <div className="save">You save {conv(old - total)} ({Math.round((1 - total / old) * 100)}% off)</div>}
            <div className="flex gap-2" style={{ margin: "12px 0 4px" }}>
              <input data-testid="coupon-input" value={couponInput} onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === "Enter" && applyCoupon()} placeholder="Coupon code" className="input-dark !py-2 text-sm" />
              {coupon ? (
                <button data-testid="coupon-remove-btn" onClick={() => { setCoupon(null); setCouponInput(""); }} className="btn-ghost !py-2 text-xs">✕</button>
              ) : (
                <button data-testid="coupon-apply-btn" onClick={applyCoupon} className="btn-outline !py-2 text-xs">APPLY</button>
              )}
            </div>
            <div style={{ margin: "14px 0" }}>
              <button className={`pay-opt ${provider === "stripe" ? "on" : ""}`} data-testid="pay-method-stripe" onClick={() => setProvider("stripe")}>
                💳 Cards & SEPA — Stripe
              </button>
              <button className={`pay-opt ${provider === "paypal" ? "on" : ""}`} data-testid="pay-method-paypal"
                onClick={() => paypalOn ? setProvider("paypal") : toast.info("PayPal isn't connected yet — add your PayPal API keys to enable it")}
                style={paypalOn ? {} : { opacity: 0.45 }}>
                🅿 PayPal{!paypalOn && " (not connected yet)"}
              </button>
              <div style={{ fontSize: 11, color: "var(--muted)" }}>Secure checkout · {currency}</div>
            </div>
            <button className="btn-primary btn-big btn-block" data-testid="checkout-btn" onClick={checkout} disabled={busy}>
              {busy ? "Redirecting…" : user ? `Checkout with ${provider === "paypal" ? "PayPal" : "Stripe"} →` : "Sign in to Checkout"}
            </button>
            <div className="guarantee">14-Day Money-Back Guarantee</div>
            <div className="secure-pay-sm" data-testid="secure-pay-cart">🔒 Secure checkout via <b>Stripe</b> &amp; <b>PayPal</b> — SSL encrypted, card details never touch our servers</div>
          </div>
        </div>
      )}
    </div>
  );
}
